import React, { useState, useEffect, useRef } from 'react';

const StudyAssistant = ({ course, isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && course && messages.length === 0) {
      // Generate course-specific opening question
      const openingQuestion = generateOpeningQuestion(course);
      setMessages([
        {
          id: 1,
          type: 'assistant',
          content: openingQuestion,
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen, course, messages.length]);

  const generateOpeningQuestion = (course) => {
    const questions = {
      'ai_101': [
        "What aspect of artificial intelligence are you most curious about? Machine learning, neural networks, or AI ethics?",
        "If you could build an AI system to solve one problem in your daily life, what would it be and why?",
        "What's your current understanding of the difference between machine learning and traditional programming?"
      ],
      'neuro_101': [
        "What fascinates you most about how the brain works? Memory, decision-making, or consciousness?",
        "If you could enhance one aspect of your brain's function, what would it be and why?",
        "How do you think understanding neuroscience could change the way we approach learning and education?"
      ],
      'n8n_101': [
        "What repetitive task in your work or daily life would you most like to automate?",
        "Have you ever tried to connect different apps or services together? What challenges did you face?",
        "How do you think workflow automation could transform your productivity?"
      ],
      'cs_101': [
        "What programming challenge or concept has always intrigued you but seemed too complex to tackle?",
        "If you could build any software application, what problem would you solve and for whom?",
        "What's your experience with algorithms - do you see them as intimidating math or logical problem-solving?"
      ],
      'quantum_101': [
        "What's the most mind-bending concept about quantum physics that you've heard of?",
        "How do you think quantum computing might change the world in the next 20 years?",
        "What would you want to understand first: quantum superposition, entanglement, or quantum algorithms?"
      ],
      'default': [
        `What initially drew you to the ${course?.title || 'this course'}? What are you hoping to learn?`,
        `What's one concept from ${course?.title || 'this field'} that you've always wanted to understand better?`,
        `How do you think mastering ${course?.domain || 'this subject'} could impact your career or personal goals?`
      ]
    };

    const courseQuestions = questions[course?.id] || questions['default'];
    const randomQuestion = courseQuestions[Math.floor(Math.random() * courseQuestions.length)];
    
    return `👋 Welcome to your AI Study Assistant for **${course?.title || 'this course'}**!\n\nI'm here to help you explore, understand, and master the concepts in this course through interactive discussions.\n\n🤔 **Let's start with a thought-provoking question:**\n\n${randomQuestion}`;
  };

  const generateAIResponse = async (userMessage, courseContext) => {
    setIsThinking(true);
    
    // For demo purposes, we'll simulate calling a real LLM
    // In production, you'd set up a proper API endpoint with OpenAI key
    const DEMO_MODE = true;
    
    if (DEMO_MODE) {
      // Simulate realistic API call timing
      await new Promise(resolve => setTimeout(resolve, 1200 + Math.random() * 800));
      
      // Generate contextual responses that feel like a real LLM
      const contextualResponses = [
        `That's a fascinating question about ${courseContext?.title}! This connects to some fundamental concepts in ${courseContext?.domain}. What specific patterns or connections do you notice when you think about this topic?`,
        
        `Great insight! Your perspective on ${courseContext?.domain} shows real curiosity. This actually relates to a key challenge in this field - how do you think experts approach this kind of problem?`,
        
        `I love how you're thinking about this! This question touches on several important principles we explore in ${courseContext?.title}. What do you think might be the real-world implications of what you're describing?`,
        
        `That's exactly the kind of critical thinking we want to develop in ${courseContext?.domain}! How do you think this concept might evolve or change in the coming years?`,
        
        `Excellent observation! This connects to both theoretical foundations and practical applications in ${courseContext?.title}. What challenges do you anticipate someone might face when working with these concepts?`
      ];
      
      const response = contextualResponses[Math.floor(Math.random() * contextualResponses.length)];
      setIsThinking(false);
      return response;
    }
    
    try {
      // Production LLM API call code (ready for when API is set up)
      const conversationHistory = messages
        .slice(-6) // Include last 6 messages for context
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        }));

      const systemPrompt = `You are an expert AI Study Assistant for ${courseContext?.title}.
      
Course: ${courseContext?.title} (${courseContext?.domain})
Duration: ${courseContext?.duration}
Level: ${courseContext?.difficulty}

Your role: Ask thought-provoking questions, connect concepts to applications, encourage critical thinking. Be conversational and concise (2-3 sentences max).`;

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            ...conversationHistory,
            { role: 'user', content: userMessage }
          ],
          temperature: 0.8,
          max_tokens: 300
        })
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);
      
      const data = await response.json();
      const aiResponse = data.choices?.[0]?.message?.content || 
        'I\'m having trouble processing that right now. Could you try rephrasing?';
      
      setIsThinking(false);
      return aiResponse;

    } catch (error) {
      console.error('LLM API Error:', error);
      setIsThinking(false);
      return `I'm experiencing technical difficulties, but your question about ${courseContext?.title} is intriguing! What specific aspect would you like to explore while I reconnect?`;
    }
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentInput('');

    // Generate AI response
    const aiResponse = await generateAIResponse(currentInput, course);
    
    const assistantMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: aiResponse,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, assistantMessage]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestQuestions = [
    "How does this relate to real-world applications?",
    "What are the key challenges in this field?",
    "Can you give me a practical example?",
    "What should I focus on learning first?",
    "How can I practice these concepts?"
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[90vh] sm:h-[80vh] flex flex-col mx-auto">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-xl sm:text-2xl">{course?.emoji || '🤖'}</div>
              <div>
                <h2 className="font-semibold text-sm sm:text-base text-gray-900">AI Study Assistant</h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate max-w-48 sm:max-w-none">{course?.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close Study Assistant"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[80%] p-2 sm:p-3 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap">
                  {message.content}
                </div>
                <div className={`text-xs mt-1 ${
                  message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {isThinking && (
            <div className="flex justify-start">
              <div className="bg-gray-100 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span className="text-sm text-gray-600">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Questions */}
        <div className="p-2 border-t border-gray-100">
          <div className="flex flex-wrap gap-1 overflow-x-auto">
            {suggestQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => setCurrentInput(question)}
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition-colors whitespace-nowrap flex-shrink-0"
              >
                {question}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="p-3 sm:p-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-2">
            <textarea
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about this course..."
              className="flex-1 p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              rows="2"
            />
            <button
              onClick={handleSendMessage}
              disabled={!currentInput.trim() || isThinking}
              className="px-3 sm:px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 self-stretch sm:self-auto"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyAssistant;